import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenWidth } from '../../../hooks/useScreenWidth';

// Components
import Samples from '../RightButtons/Samples/Samples';
import StudioChannelPopupDelete from '../StudioChannelPopup/StudioChannelPopupDelete';
import StudioChannelPopupTariff from '../StudioChannelPopup/StudioChannelPopupTariff';
import SettingsPopup from '../SettingsPopup';
import StudioErrorsContainer from '../StudioErrorsContainer';
import Scenes from '../RightButtons/ScenesElements/ScenesElements';
import Scene from '../Scene';
import FunctionalButtons from '../FunctionalButtons';
import RightButtons from '../RightButtons';
import AddChannelPopup from '../../../components/AddChannelPopup';
import { EditChannelPopup } from '../../../components/EditChannelPopup';
import Layout from '../../../components/Layout';
import Channels from '../../../components/Stream/Channels';
import Chat from '../../../components/Stream/Chat';
import StreamPreview from '../../../components/StreamPreview/StreamPreview';
import EditPlatformPopup from '../../../components/EditPlatformPopup';

// Hooks
import { apiSlice, CHANNELS_TAG, useGetStudioQuery } from '../../../features/api/apiSlice';
import { ChannelStatusProvider } from '../../../components/Stream/Channels/ChannelStatusContext';
import useStudioChannel from './useStudioChannel';
import { SelectedPlatformsProvider } from '../../../components/SelectPlatform/SelectedPlatformsContext';

// Type && Styles
import { IStudioPage, StudioRightButtons } from './Studio.interface';
import styles from "./Studio.module.css";
import MobileStudio from './MobileStudio/MobileStudio';
import { getStreamOrientation } from '../../../utils/streamOrientation';
import { IPostMessageData } from '../../Broadcast/Broadcast.interface';
import { useAppDispatch } from '../../../app/hooks';

function StudioPage({ connectionsParams }: IStudioPage) {
  const { isMobile } = useScreenWidth();
  const { data: studio, error: templatesError, isLoading: isLoadingTemplates } = useGetStudioQuery();
  const navigate = useNavigate();
  const [activeBlock, setActiveBlock] = useState<StudioRightButtons | null>(null);
  const { getPlayerWrapperClasses, getPlayerClasses } = getStreamOrientation(studio);
  const dispatch = useAppDispatch();
  const { REACT_APP_API_ENDPOINT } = process.env;

  const {
    channelsProps,
    isShowAddChannelPopup,
    channelToEdit,
    channelToDelete,
    enableChannelError,
    isShowSettingsPopup,
    handleClosePopupAddChannel,
    handleClosePopupEditChannel,
    handleClosePopupDelete,
    handleDeleteChannel,
    setEnableChannelError,
    setIsShowSettingsPopup,
    user,
    isBroadcastOnline,
    studioStatus
  } = useStudioChannel(connectionsParams);

  const toggleActiveBlock = (active: StudioRightButtons | null) => {
    setActiveBlock((prev) => (prev === active ? null : active));
  };

  const { serverUrl, token } = connectionsParams || {};

  const rightPanelComponents = {
    chat: (
      <div className={styles["studio__chat"]}>
        <Chat studioBroadcastId={studio?.broadcast.id} studioTemplates={studio} />
      </div>
    ),
    channels: (
      <div className={styles["studio__channels"]}>
        <Channels
          {...channelsProps}
          isStudio={true}
          studioBroadcastId={studio?.broadcast.id}
          channels={studio?.broadcast.restream_templates}
          studio={studio}
        />
      </div>
    ),
    samples: (
      <Samples
        isLoading={isLoadingTemplates}
        error={Boolean(templatesError)}
        templates={studio ? studio.studio_templates : []}
      />
    ),
    scenes: (
      <Scenes background={studio?.backgrounds} logos={studio?.logos} studio={studio} />
    )
  };

  const renderEditChannelPopup = () => {
    if (!channelToEdit) return null;

    return channelToEdit.api_authorized ? (
      <EditPlatformPopup
        tariff={user?.tariff?.name}
        isShow={true}
        onClose={handleClosePopupEditChannel}
        channel={channelToEdit}
        isBroadcastOnline={isBroadcastOnline}
        studio={studio}
      />
    ) : (
      <EditChannelPopup
        isShow={true}
        onClose={handleClosePopupEditChannel}
        channel={channelToEdit}
        studio={studio}
      />
    );
  };

  useEffect(() => {
      const handleMessage = (event: MessageEvent<IPostMessageData>) => {
          const eventData = event.data;
          const isOriginValid = event.origin === REACT_APP_API_ENDPOINT;
          const isChannelAuth = eventData.channel_auth;

          if (!isOriginValid || !isChannelAuth) return;
          dispatch(apiSlice.util.invalidateTags([CHANNELS_TAG]));
      };

      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <SelectedPlatformsProvider>
      <ChannelStatusProvider broadcastId={studio?.broadcast.id}>
        <Layout>
          {isMobile ? (
            <MobileStudio
              studio={studio}
              studioStatus={studioStatus}
              setIsShowSettingsPopup={setIsShowSettingsPopup}
              channelsProps={channelsProps}
              studioBroadcastId={studio?.broadcast.id}
              isLoadingTemplates={isLoadingTemplates}
              templatesError={templatesError}
              user={user}
            />
          ) : (
            <div className={styles.studio}>
              <div className={styles["studio__menu"]}>
                <Scene user={user}/>
              </div>
                <div className={styles["studio__container"]}>
                  {serverUrl && token && (
                    <div className={getPlayerWrapperClasses(styles["studio__player-wrapper"])}>
                      <StreamPreview
                        currentTemplate={studio?.current_scene.current_template}
                        background={studio?.current_scene.background}
                        logo={studio?.current_scene.logo}
                        className={getPlayerClasses(styles["studio__player"])}
                      />
                    </div>
                  )}
                <StudioErrorsContainer />

                <div className={styles["studio__functional-buttons"]}>
                  <FunctionalButtons
                    onSettings={() => setIsShowSettingsPopup(true)}
                    studioStatus={studioStatus}
                  />
                </div>
              </div>

              {activeBlock && rightPanelComponents[activeBlock as keyof typeof rightPanelComponents]}

              <div className={styles["studio__right-block"]}>
                <RightButtons
                  toggleChat={() => toggleActiveBlock("chat")}
                  toggleChannels={() => toggleActiveBlock("channels")}
                  toggleSamples={() => toggleActiveBlock("samples")}
                  toggleScenes={() => toggleActiveBlock("scenes")}
                  user={user}
                />
              </div>
            </div>
          )}

          {isShowAddChannelPopup && (
            <AddChannelPopup
              user={user}
              isShow={isShowAddChannelPopup}
              onClose={handleClosePopupAddChannel}
            />
          )}

          {channelToDelete && (
            <StudioChannelPopupDelete
              isShow={true}
              onClose={handleClosePopupDelete}
              channelName={channelToDelete?.name}
              onDelete={handleDeleteChannel}
            />
          )}

          {renderEditChannelPopup()}

          {enableChannelError && (
            <StudioChannelPopupTariff
              isShow={true}
              onClose={() => setEnableChannelError("")}
              errorMessage={enableChannelError}
              onTariffChange={() => navigate("/tariffs")}
            />
          )}

          <SettingsPopup
            onClose={() => setIsShowSettingsPopup(false)}
            isShow={isShowSettingsPopup}
            studio={studio}
          />
        </Layout>
      </ChannelStatusProvider>
    </SelectedPlatformsProvider>
  );
}

export default StudioPage;